import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Lion, Wordmark } from '@/components/brand/Lion'
import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'
import { useTheme } from '@/lib/theme'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { NAVIGATION, type NavGroup, type NavItem } from './navigation'

/**
 * The internal frame: a soft grey rail on the left, the white page on the
 * right. The current page is marked in the action blue.
 *
 * On a phone the rail becomes a sheet behind a menu button, because she will
 * open this at a venue as often as at her desk.
 */
export function AppShell() {
  const location = useLocation()
  const [railOpen, setRailOpen] = useState(false)

  // Any navigation closes the mobile rail.
  useEffect(() => {
    setRailOpen(false)
  }, [location.pathname])

  return (
    <div className="flex min-h-dvh">
      <Rail open={railOpen} onClose={() => setRailOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenRail={() => setRailOpen(true)} />
        <main id="main" className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function Rail({ open, onClose }: { open: boolean; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    closeRef.current?.focus()

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-ink/20 lg:hidden"
        />
      ) : null}

      <nav
        aria-label="Studio"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[var(--size-rail)] shrink-0 flex-col border-r bg-paper',
          'transition-transform duration-[var(--dur-base)] ease-[var(--ease-out)]',
          open ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:translate-x-0',
        )}
      >
        <div className="flex h-[var(--size-topbar)] shrink-0 items-center justify-between pr-2 pl-5">
          <Wordmark />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="grid size-8 place-items-center rounded-md hover:bg-[var(--color-wash-strong)] lg:hidden"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="m1 1 10 10M11 1 1 11" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          <NavGroups />
        </div>

        <div className="shrink-0 px-3 py-2">
          <ThemeSwitch />
        </div>

        <div className="shrink-0 border-t border-t-[var(--border-hairline)] p-3">
          <AccountBlock />
        </div>
      </nav>
    </>
  )
}

/** The group holding the current page, if any. */
function activeGroup(pathname: string): string | null {
  const under = (path: string) => pathname === path || pathname.startsWith(`${path}/`)
  const matches = (item: NavItem) => (item.end ? pathname === item.to : under(item.to)) || (item.also ?? []).some(under)
  return (
    NAVIGATION.find((group) => (group.to && (under(group.to) || (group.also ?? []).some(under))) || group.items.some(matches))?.title ?? null
  )
}

const HEADING =
  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.08em]'

/**
 * The menu (28 Oct 2026). Operations and Personal HQ are always open; the
 * other sections are drop-downs, one open at a time. Clicking a drop-down
 * heading opens it and goes straight to its first page; clicking it again
 * while open only closes it. Contacts and Settings are headings that are
 * their own page.
 */
function NavGroups() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const current = activeGroup(pathname)
  const [open, setOpen] = useState<string | null>(current)

  useEffect(() => {
    if (current) setOpen(current)
  }, [current])

  return NAVIGATION.map((group) => {
    const id = `nav-${group.title.toLowerCase().replaceAll(' ', '-')}`

    if (group.to) {
      return (
        <div key={group.title} className="mb-1 px-3">
          <NavLink
            to={group.to}
            className={cn(
              HEADING,
              'transition-colors duration-[var(--dur-fast)]',
              current === group.title ? 'bg-[var(--color-accent-wash)] text-accent' : 'text-ink hover:bg-[var(--color-wash-strong)]',
            )}
          >
            {group.title}
          </NavLink>
        </div>
      )
    }

    if (group.pinned) {
      return (
        <div key={group.title} className="mb-1 px-3">
          <p id={id} className={cn(HEADING, 'text-ink')}>
            {group.title}
          </p>
          <NavList group={group} labelledBy={id} />
        </div>
      )
    }

    const expanded = open === group.title
    const first = group.items[0]
    return (
      <div key={group.title} className="mb-1 px-3">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => {
            if (expanded) {
              setOpen(null)
              return
            }
            setOpen(group.title)
            if (first && current !== group.title) navigate(first.to)
          }}
          className={cn(
            HEADING,
            'transition-colors duration-[var(--dur-fast)] hover:bg-[var(--color-wash-strong)]',
            !expanded && current === group.title ? 'text-accent' : 'text-ink',
          )}
        >
          <span className="flex-1">{group.title}</span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden
            className={cn('shrink-0 text-taupe transition-transform duration-[var(--dur-fast)]', expanded && 'rotate-90')}
          >
            <path d="m3.5 1.5 3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {expanded ? <NavList group={group} id={id} /> : null}
      </div>
    )
  })
}

function NavList({ group, id, labelledBy }: { group: NavGroup; id?: string; labelledBy?: string }) {
  const { pathname } = useLocation()
  return (
    <ul id={id} aria-labelledby={labelledBy} className="mb-2 mt-0.5">
      {group.items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end ?? false}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md py-[7px] pr-2.5 pl-5 text-[length:var(--text-sm)]',
                'transition-colors duration-[var(--dur-fast)]',
                isActive || (item.also ?? []).some((path) => pathname === path || pathname.startsWith(`${path}/`))
                  ? 'bg-[var(--color-accent-wash)] font-semibold text-accent'
                  : 'text-ink hover:bg-[var(--color-wash-strong)]',
              )
            }
          >
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  )
}

/** Light or dark, at the foot of the rail. A two-state switch, not a menu. */
function ThemeSwitch() {
  const { theme, toggle } = useTheme()
  const dark = theme === 'dark'

  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      onClick={toggle}
      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[length:var(--text-sm)] text-ink transition-colors duration-[var(--dur-fast)] hover:bg-[var(--color-wash-strong)]"
    >
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden className="shrink-0">
        {dark ? (
          <path d="M12 8.6A5.3 5.3 0 0 1 5.4 2a5.3 5.3 0 1 0 6.6 6.6Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
        ) : (
          <>
            <circle cx="7" cy="7" r="2.6" stroke="currentColor" strokeWidth="1.1" />
            <path d="M7 1v1.4M7 11.6V13M1 7h1.4M11.6 7H13M2.8 2.8l1 1M10.2 10.2l1 1M2.8 11.2l1-1M10.2 3.8l1-1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
          </>
        )}
      </svg>
      <span className="flex-1 text-left">Dark mode</span>
      {/* A small round switch, blue when on. */}
      <span aria-hidden className={cn('relative h-4 w-7 shrink-0 rounded-full transition-colors', dark ? 'bg-accent' : 'bg-[var(--color-rule-strong)]')}>
        <span
          className={cn(
            'absolute top-[2px] size-3 rounded-full bg-white transition-[left] duration-[var(--dur-base)] ease-[var(--ease-out)]',
            dark ? 'left-[14px]' : 'left-[2px]',
          )}
        />
      </span>
    </button>
  )
}

function AccountBlock() {
  const { staff, signOut } = useCurrentStaff()

  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="grid size-7 shrink-0 place-items-center rounded-full bg-accent font-[family-name:var(--font-heading)] text-[0.6875rem] font-semibold text-on-accent"
      >
        {initials(staff.full_name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[length:var(--text-xs)] leading-tight">{staff.full_name}</p>
        <p className="label leading-tight">{staff.role}</p>
      </div>
      <button
        type="button"
        onClick={() => void signOut()}
        title="Sign out"
        aria-label="Sign out"
        className="grid size-7 shrink-0 place-items-center rounded-md text-taupe hover:bg-[var(--color-wash-strong)] hover:text-ink"
      >
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M5.5 12.5H2.2a.7.7 0 0 1-.7-.7V2.2a.7.7 0 0 1 .7-.7h3.3M9.3 9.8 12.5 7 9.3 4.2M12.5 7H5.2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  )
}

function TopBar({ onOpenRail }: { onOpenRail: () => void }) {
  return (
    <div className="sticky top-0 z-20 flex h-[var(--size-topbar)] shrink-0 items-center gap-3 bg-cream/95 px-4 backdrop-blur-sm lg:hidden">
      <button
        type="button"
        onClick={onOpenRail}
        aria-label="Open menu"
        className="grid size-8 place-items-center hover:bg-[var(--color-wash-strong)] lg:hidden"
      >
        <svg width="15" height="12" viewBox="0 0 15 12" fill="none" aria-hidden>
          <path d="M0 1h15M0 6h15M0 11h15" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </button>

      <Lion size={20} className="lg:hidden" />

      <div className="flex-1" />
    </div>
  )
}
